import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { DirectedScript } from 'fym-shared/src/music24/directedVideoApi';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../music24/components/primitives/SMLButton';
import { formatTime } from '../../../musicVideoEditor/timeline/timelineGeometry';
import { useDirectedVideoContext } from '../../DirectedVideoContext';
import { directorMessages } from '../../messages/directorMessages';
import { scriptMessages as messages } from '../../messages/scriptMessages';
import { DirectorRuns } from '../director/DirectorRuns';
import { EffectFamilyChips } from '../director/EffectFamilyChips';
import { scriptFacts } from './scriptEdits';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the script step; each part uses its own slice of it */
import styles from './Script.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import steps from '../DirectorSteps.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import shell from '../../DirectedVideo.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ScriptSideProps {
  script: DirectedScript;
  /** The "Rewrite the whole script?" panel takes the place of the facts (design e2). */
  isRewriteOpen: boolean;
  rewriteDisabled: boolean;
  onRewrite: (note: string) => void;
  onCloseRewrite: () => void;
}

/**
 * The side panel of the script (design e1): the shots, the new shots, the length, the effects and
 * the lyric moments, the story and the style, the effect colours and the director runs. Or the
 * "Rewrite the whole script?" panel with its note (e2).
 */
const ScriptSideComponent: React.FC<ScriptSideProps> = ({
  script,
  isRewriteOpen,
  rewriteDisabled,
  onRewrite,
  onCloseRewrite,
}) => {
  const intl = useIntl();
  const { video, song } = useDirectedVideoContext();
  const [note, setNote] = useState('');
  const facts = scriptFacts(script);
  const durationSec = song.durationSec ?? facts.endSec;
  const coversSong = Math.abs(facts.endSec - durationSec) < 0.5;

  if (isRewriteOpen) {
    return (
      <>
        <h4>{intl.formatMessage(messages.rewriteAllTitle)}</h4>
        <div className={steps.summary}>
          <p className={steps.summaryText}>{intl.formatMessage(messages.rewriteAllText)}</p>
          <input
            className={steps.input}
            value={note}
            placeholder={intl.formatMessage(messages.rewriteAllNote)}
            aria-label={intl.formatMessage(directorMessages.noteLabel)}
            onChange={(event) => setNote(event.target.value)}
          />
          <div className={styles.editorLine}>
            <SMLButton
              variant="primary"
              className={steps.small}
              disabled={rewriteDisabled}
              onClick={() => onRewrite(note)}
            >
              {intl.formatMessage(messages.rewriteAllConfirm)}
            </SMLButton>
            <SMLButton className={clsx(steps.small, steps.ghost)} onClick={onCloseRewrite}>
              {intl.formatMessage(messages.cancel)}
            </SMLButton>
          </div>
        </div>
        <span className={shell.grow} />
        <DirectorRuns left={video.directorRunsLeft} withText={false} />
      </>
    );
  }

  return (
    <>
      <h4>{intl.formatMessage(messages.sideTitle)}</h4>
      <dl className={styles.factList}>
        <dt>{intl.formatMessage(messages.factShots)}</dt>
        <dd>{facts.shots}</dd>
        <dt>{intl.formatMessage(messages.factNewShots)}</dt>
        <dd>
          {facts.reused > 0
            ? intl.formatMessage(messages.factNewShotsValue, { count: facts.clips, reused: facts.reused })
            : facts.clips}
        </dd>
        <dt>{intl.formatMessage(messages.factLength)}</dt>
        <dd>
          {coversSong
            ? intl.formatMessage(messages.factLengthWhole, { length: formatTime(durationSec) })
            : formatTime(facts.endSec)}
        </dd>
        <dt>{intl.formatMessage(messages.factEffects)}</dt>
        <dd>{facts.effects}</dd>
        <dt>{intl.formatMessage(messages.factLyrics)}</dt>
        <dd>{facts.lyricMoments}</dd>
      </dl>
      {video.story !== null && (
        <div className={steps.summary}>
          <span className={steps.summaryEyebrow}>{intl.formatMessage(directorMessages.sideStory)}</span>
          <span className={steps.summaryTitle}>{video.story.title}</span>
        </div>
      )}
      {video.style !== null && (
        <div className={steps.summary}>
          <span className={steps.summaryEyebrow}>{intl.formatMessage(directorMessages.sideStyle)}</span>
          <span className={steps.summaryTitle}>{video.style.title}</span>
          <p className={steps.summaryText}>{intl.formatMessage(messages.styleInPrompt)}</p>
          <span className={steps.summaryEyebrow}>{intl.formatMessage(messages.styleEffects)}</span>
          <EffectFamilyChips families={video.style.effectFamilies} label={intl.formatMessage(messages.styleEffects)} />
          {!video.style.effectFamilies.includes('lyric') && (
            <p className={steps.summaryText}>{intl.formatMessage(messages.styleNoLyrics)}</p>
          )}
        </div>
      )}
      <div className={steps.summary}>
        <span className={steps.summaryEyebrow}>{intl.formatMessage(messages.effectColours)}</span>
        <div className={styles.chipLegend}>
          <span className={styles.fxChip}>{intl.formatMessage(messages.colourTitles)}</span>
          <span className={clsx(styles.fxChip, styles.fxChipTransition)}>
            {intl.formatMessage(messages.colourTransitions)}
          </span>
          <span className={clsx(styles.fxChip, styles.fxChipLyric)}>{intl.formatMessage(messages.colourLyrics)}</span>
        </div>
      </div>
      <span className={shell.grow} />
      <DirectorRuns left={video.directorRunsLeft} withText={false} />
      <p className={shell.hint}>{intl.formatMessage(messages.sideHint)}</p>
    </>
  );
};

export const ScriptSide = TypedMemo(ScriptSideComponent);
